import type { ApiConfig } from '@blufin/types';
import { LOCAL_BUILD_VERSION_PREFIX, VALID_ENVIRONMENTS } from '@blufin/types';
import { AbstractEnvironmentClientAlpha } from './abstract/AbstractEnvironmentClientAlpha';
import type { BaseRuntimeConfig, RuntimeConfig } from './ReactEnvironmentClientAlpha';

/**
 * Next.js-specific environment client interface.
 * Similar to IReactEnvironmentClientAlpha but without the Vite-specific 'mode' property.
 */
export interface INextJsEnvironmentClientAlpha<TCustom extends object = {}> {
  /** HTTP client configuration from config */
  readonly apiConfig: ApiConfig;
  /** Custom app configuration fields (excludes base fields) */
  readonly appConfig: RuntimeConfig<TCustom>;
  /** Deployment environment from config */
  readonly environment: string;
  /** Get all environment variables as a single object */
  getAllEnvironmentVariables(): Omit<INextJsEnvironmentClientAlpha<TCustom>, 'getAllEnvironmentVariables'>;
  /** App version from build (calendar format: YYYY.WW.PATCH) */
  readonly version: string;
}

/**
 * Next.js environment client for loading configuration from NEXT_PUBLIC_APP_CONFIG.
 * Reads runtime config passed directly (parsed from env var at build time).
 * Unlike ReactEnvironmentClientAlpha, does not depend on Vite's import.meta.env.
 */
export class NextJsReactEnvironmentClientAlpha<TCustom extends object = {}>
  extends AbstractEnvironmentClientAlpha<RuntimeConfig<TCustom>>
  implements INextJsEnvironmentClientAlpha<TCustom>
{
  /**
   * Creates an instance of NextJsReactEnvironmentClientAlpha.
   * @param runtimeConfig - The runtime configuration parsed from NEXT_PUBLIC_APP_CONFIG.
   * @param version - Optional app version (defaults to LOCAL_BUILD_VERSION_PREFIX).
   */
  constructor(runtimeConfig: BaseRuntimeConfig<TCustom>, version?: string) {
    const { environment, apiConfig, appConfig = {} as TCustom } = runtimeConfig;
    super({ apiConfig, appConfig, environment, version: version || LOCAL_BUILD_VERSION_PREFIX });
    this._validateRuntimeConfig(runtimeConfig);
  }

  /**
   * Gets all environment variables as a single object.
   */
  public getAllEnvironmentVariables(): Omit<INextJsEnvironmentClientAlpha<TCustom>, 'getAllEnvironmentVariables'> {
    return {
      apiConfig: this.apiConfig,
      appConfig: this.appConfig,
      environment: this.environment,
      version: this.version
    };
  }

  /**
   * Validates that the runtime configuration contains all required base fields.
   * @param config - The runtime config to validate.
   */
  private _validateRuntimeConfig(config: BaseRuntimeConfig): void {
    this.validateRequiredField(config.environment, 'environment');
    this.validateRequiredField(config.apiConfig, 'apiConfig');
    this.validateEnvironmentValue(config.environment, VALID_ENVIRONMENTS as string[]);
  }
}
